<template>
  <div class="list-orders-page">
    <PageHeader
      :title="t('myShopData.orders.title')"
      summary=""
      :back-link-to="{ name: 'shop.system-objects' }"
      :back-link-text="t('navbar.systemData')"
    />

    <div class="page-content">
      <div class="ListsOrders">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :columns="columns"
          :initial-sort="initialSorting"
          :show-reload-button="true"
          data-key="id"
          :enable-grouped-actions="true"
          :exportable="true"
          :show-export-all-button="true"
          :export-config="{ entityType: 'orders' }"
          :persistent-filters="persistentFilters"
          @export="handleExport"
        >
          <!-- Composite column: order_info (order_reference, id_order, dates) -->
          <template #cell-order_info="{ data }">
            <div class="fullname-cell">
              <div class="name-row">
                <span
                  v-if="cleanStr(data.reference)"
                  class="contact-name"
                >
                  {{ cleanStr(data.reference) }}
                </span>
                <span
                  v-else
                  class="contact-name contact-name--empty"
                >
                  {{ t('myShopData.orders.noReference') }}
                </span>
                <DataExplorerLink
                  :id="data.id"
                  :value="`#${data.order_id}`"
                  :type="DataExplorerDataType.ORDER_FILE"
                  :show-icon="false"
                  class-name="id-badge"
                />
              </div>
              <div class="dates-row">
                <SpmDateDisplay
                  :value="data.date"
                  format="date"
                  icon="fa-light fa-calendar-day"
                  size="sm"
                />
                <SpmDateDisplay
                  :value="data.updated_at"
                  format="date"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </div>
            </div>
          </template>

          <!-- Single column: created_at -->
          <template #cell-created_at="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <SpmDateDisplay
                :value="data.created_at"
                format="date"
                icon="fa-light fa-arrows-rotate"
                size="sm"
              />
            </div>
          </template>

          <!-- Composite column: customer_info (firstname, lastname, email) -->
          <template #cell-customer_info="{ data }">
            <div
              v-if="data.customer_deleted === 0 || data.customer_deleted === '0'"
              class="customer-cell"
            >
              <div class="customer-name">
                <span
                  v-if="formatFullName(data.customer_first_name, data.customer_last_name)"
                  class="contact-name"
                >
                  {{ formatFullName(data.customer_first_name, data.customer_last_name) }}
                </span>
                <span
                  v-else
                  class="contact-name contact-name--empty"
                >
                  {{ t('myShopData.orders.noCustomerName') }}
                </span>
              </div>
              <div
                v-if="cleanStr(data.customer_email)"
                class="customer-email"
              >
                {{ cleanStr(data.customer_email) }}
              </div>
            </div>
            <div
              v-else
              class="customer-cell customer-cell--deleted"
            >
              <i
                v-tooltip.top="t('myShopData.orders.anonUserInfo')"
                class="fa-regular fa-mask"
              />
              <span class="deleted-text">{{ t('myShopData.orders.deletedCustomer') }}</span>
            </div>
          </template>

          <!-- Composite column: amounts (amount, amount_without_tax, currency) -->
          <template #cell-amounts="{ data }">
            <div class="amounts-cell">
              <div class="amount-row">
                <span class="amount-label">{{ t('myShopData.orders.headers.amount') }}:</span>
                <span class="amount-value">{{ formatAmount(data.amount, data.currency) }}</span>
              </div>
              <div class="amount-row amount-row--secondary">
                <span class="amount-label">{{ t('myShopData.orders.headers.amount_without_tax') }}:</span>
                <span class="amount-value">{{ formatAmount(data.amount_without_tax, data.currency) }}</span>
              </div>
            </div>
          </template>

          <!-- Single column: lang -->
          <template #cell-lang="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span
                v-if="cleanStr(data.lang)"
                v-tooltip.top="t(`languages.codes.${cleanStr(data.lang)}`)"
                class="lang-badge cursor-pointer"
              >{{ cleanStr(data.lang).toUpperCase() }}</span>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: is_confirmed -->
          <template #cell-is_confirmed="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="data.is_confirmed === 1 || data.is_confirmed === '1'"
                v-tooltip.top="t('myShopData.orders.confirmed')"
                class="fas fa-check-circle cell-icon cell-icon--active"
              />
              <i
                v-else
                v-tooltip.top="t('myShopData.orders.notConfirmed')"
                class="fas fa-times-circle cell-icon cell-icon--inactive"
              />
            </div>
          </template>
        </spmtableapi>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import DataExplorerLink from '@/components/data-explorer/fields/DataExplorerLink.vue';
import { NestApiType } from '@/composables/nestApi';
import { DataExplorerDataType } from '@/types';
import { currencies } from '@/configs/currencies';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
  SpmActiveSort,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';
import { formatAmount } from '@/components/table-rest-api/formatters';

export default defineComponent({
  name: 'OrdersV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    DataExplorerLink,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const userLocale = ref<string>();

    // Currency list for filters
    const currencyList = currencies.map((currency: {code: string; label: string}) => ({
      value: currency.code,
      label: currency.label,
    }));

    const initialSorting: SpmActiveSort = { field: 'updated_at', order: 'desc' };

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/orders',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'order_id',
        header: '',
        fields: [{
          name: 'order_id',
          label: t('myShopData.orders.headers.id_order'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'order_info',
        header: t('myShopData.orders.headers.order_reference'),
        fields: [{
          name: 'reference',
          label: t('myShopData.orders.headers.order_reference'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'date',
          label: t('myShopData.orders.headers.date'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.orders.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'created_at',
        header: t('myShopData.orders.headers.date_creation'),
        fields: [{
          name: 'created_at',
          label: t('myShopData.orders.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'customer_info',
        header: t('myShopData.orders.headers.customer'),
        fields: [{
          name: 'customer_first_name',
          label: t('myShopData.orders.headers.first_name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'customer_last_name',
          label: t('myShopData.orders.headers.last_name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'customer_email',
          label: t('myShopData.orders.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'customer_deleted',
          label: t('myShopData.orders.headers.deleted'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { value: '0', label: t('myShopData.orders.headers.deleted') },
              { value: '1', label: t('myShopData.orders.headers.notDeleted') },
            ],
          },
        }],
      },
      {
        id: 'amounts',
        header: t('myShopData.orders.headers.amounts'),
        fields: [{
          name: 'amount',
          label: t('myShopData.orders.headers.amount'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'numberInput',
          },
        }, {
          name: 'amount_without_tax',
          label: t('myShopData.orders.headers.amount_without_tax'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'numberInput',
          },
        }, {
          name: 'currency',
          label: t('myShopData.orders.headers.currency'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: currencyList,
          },
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.orders.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.orders.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'is_confirmed',
        header: t('myShopData.orders.headers.is_valid'),
        fields: [{
          name: 'is_confirmed',
          label: t('myShopData.orders.headers.is_valid'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { value: '1', label: t('active') },
              { value: '0', label: t('inactive') },
            ],
          },
        }],
      },
    ];

    // Helper function to clean strings
    const cleanStr = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return String(value).trim();
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // Wrapper to use formatAmount with user locale
    const formatAmountWithLocale = (amount: number | string | null | undefined, currency: string): string => formatAmount(amount, currency, userLocale.value || 'fr-FR');

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO : Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/orders',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myShopData.orders.export.success'));
        // } else {
        //   await showToastError(t('myShopData.orders.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myShopData.orders.export.error'));
      }
    };

    return {
      t,
      apiCall,
      columns,
      persistentFilters,
      cleanStr,
      formatFullName,
      formatAmount: formatAmountWithLocale,
      handleExport,
      initialSorting,
      DataExplorerDataType,
    };
  },
});
</script>

<style scoped>
/* Order info composite column */
.fullname-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fullname-cell .name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fullname-cell .contact-name {
  font-weight: 600;
  color: #1f2937;
}

/* Style badge pour les IDs cliquables */
:deep(.id-badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px !important;
  margin: 0 !important;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.2;
  text-decoration: none !important;
  transition: all 0.2s ease;
  gap: 0 !important;
}

:deep(.id-badge span) {
  text-decoration: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

:deep(.id-badge i) {
  display: none !important;
}

:deep(.id-badge:hover) {
  background: #bae6fd;
  color: #075985;
  text-decoration: none !important;
}

:deep(.id-badge:hover span) {
  text-decoration: none !important;
}

.fullname-cell .contact-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}

.fullname-cell .dates-row {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #6b7280;
}

/* Customer info composite column */
.customer-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.customer-cell .customer-name {
  font-weight: 500;
  color: #1f2937;
}

.customer-cell .customer-email {
  font-size: 0.875rem;
  color: #6b7280;
}

.customer-cell--deleted {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  color: #9ca3af;
  font-style: italic;
}

.customer-cell--deleted i {
  font-size: 1rem;
  color: #d1d5db;
}

.customer-cell--deleted .deleted-text {
  font-size: 0.875rem;
}

/* Amounts composite column */
.amounts-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.amount-row {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.amount-row .amount-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.amount-row .amount-value {
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 600;
}

.amount-row--secondary .amount-label {
  color: #9ca3af;
}

.amount-row--secondary .amount-value {
  font-weight: 500;
  color: #6b7280;
}

/* Language badge */
.lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 3px;
  letter-spacing: 0.025em;
}

/* Cell icons */
.cell-icon {
  font-size: 1rem;
  display: block;
  text-align: center;
}

.cell-icon--active {
  color: #94c840;
}

.cell-icon--inactive {
  color: #d1d5db;
}

/* Empty cell */
.cell-empty {
  color: #9ca3af;
}

.cursor-pointer {
  cursor: pointer;
}
</style>

<style scoped lang="scss">
/* Page layout */
.list-orders-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
